// The Model tab of chat's settings: a search over the provider's catalog and
// its models by tier, each row able to become the model chat answers with
// ("Use") or the one its background steps run on ("Use for tasks"). Both
// models in use sit at the top under "In use", tagged; the task model is
// automatic until one is picked.
import { tierLabel } from "../lib/modelCatalog.js";
import Button from "./ui/Button.jsx";
import Highlight from "./ui/Highlight.jsx";
import HelpTip from "./ui/HelpTip.jsx";
import SearchInput from "./ui/SearchInput.jsx";

export const IN_USE_TIER = "current";

const IN_USE_HELP = "Chat is the model that answers you. Tasks is the model chat uses for steps it runs in the background, such as pulling facts out of results and checking its answers: usually a cheaper, faster one. Left automatic, Origin picks it from your provider.";

function formatCost(cost) {
    if (!cost) return null;
    const fmt = (n) => {
        if (n === 0) return "0";
        if (n >= 1) return `$${n.toFixed(2).replace(/\.?0+$/, "")}`;
        return `$${n.toFixed(3).replace(/\.?0+$/, "")}`;
    };
    return `${fmt(cost.input)} / ${fmt(cost.output)} per M`;
}

function formatContext(n) {
    if (!n) return null;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
    if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
    return String(n);
}

function Tag({ accent = false, children }) {
    return (
        <span className={`inline-flex h-[18px] shrink-0 items-center rounded-[5px] border px-[6px] text-[11px] leading-none ${accent ? "border-[color:var(--green)] text-[color:var(--green)]" : "border-[color:var(--line-strong)] text-[color:var(--muted)]"}`}>
            {children}
        </span>
    );
}

function ModelRow({ model, query, isDefault, isChat, isTasks, taskIsAuto, onUse, onUseForTasks, disabled }) {
    const meta = [
        formatContext(model.contextWindow) ? `${formatContext(model.contextWindow)} context` : null,
        formatCost(model.cost),
        model.reasoning ? "Reasoning" : null,
        model.input?.includes("image") ? "Images" : null,
    ].filter(Boolean);
    // The search matches names and ids alike: where it found only the id,
    // the id shows in place of the details so the reader sees why.
    const terms = query ? [query] : [];
    const byIdOnly = query && !model.name.toLowerCase().includes(query) && model.id.toLowerCase().includes(query);
    return (
        <div
            className={`group/row grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-[12px] rounded-[7px] px-[8px] py-[7px] ${isChat ? "bg-[color-mix(in_srgb,var(--ink)_8%,transparent)]" : "hover:bg-[color-mix(in_srgb,var(--ink)_5%,transparent)]"}`}
            data-model-row={model.id}
        >
            <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-[6px]">
                    <span className={`truncate text-[13px] text-[color:var(--ink)] ${isChat ? "font-medium" : ""}`}><Highlight text={model.name} terms={terms} /></span>
                    {isChat ? <Tag accent>Chat</Tag> : null}
                    {isTasks ? <Tag>Tasks</Tag> : null}
                    {isTasks && taskIsAuto ? <span className="shrink-0 text-[11px] text-[color:var(--muted-soft)]">automatic</span> : null}
                    {isDefault ? <span className="shrink-0 text-[11px] text-[color:var(--muted-soft)]">Provider default</span> : null}
                </div>
                <div className={`flex min-w-0 gap-x-[12px] overflow-hidden whitespace-nowrap text-[11.5px] text-[color:var(--muted-soft)] ${byIdOnly ? "font-mono" : ""}`}>
                    {byIdOnly ? <span className="truncate"><Highlight text={model.id} terms={terms} /></span>
                        : meta.length ? meta.map((part) => <span key={part} className="shrink-0">{part}</span>)
                            : <span className="truncate">{model.id}</span>}
                </div>
            </div>
            <div className="flex items-center gap-[2px] opacity-0 transition-opacity group-focus-within/row:opacity-100 group-hover/row:opacity-100 [@media(hover:none)]:opacity-100" data-model-actions="">
                {isChat ? null : <Button variant="ghost" size="sm" disabled={disabled} onClick={onUse} aria-label={`Use ${model.name}`}>Use</Button>}
                {isTasks && taskIsAuto ? null : (
                    <Button variant="ghost" size="sm" disabled={disabled} onClick={onUseForTasks} aria-label={isTasks ? "Pick the task model automatically again" : `Use ${model.name} for tasks`}>
                        {isTasks ? "Back to automatic" : "Use for tasks"}
                    </Button>
                )}
            </div>
        </div>
    );
}

export default function ChatModelList({
    searchRef, search, onSearch, groups, chatModelId, taskModelId, taskIsAuto, onUse, onUseForTasks, disabled = false,
}) {
    return (
        <>
            <div className="shrink-0 px-[18px] pb-[12px]">
                <SearchInput inputRef={searchRef} value={search} onChange={onSearch} placeholder="Search models" />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto border-t border-[color:var(--line)] px-[10px] pb-[12px]">
                {groups.length === 0 ? (
                    <p className="m-0 px-[8px] py-[16px] text-center text-[12.5px] text-[color:var(--muted)]">No models match. Try another name.</p>
                ) : groups.map((g) => (
                    <div key={g.tier} role="group" aria-label={g.tier === IN_USE_TIER ? "In use" : tierLabel(g.tier)}>
                        <div className="flex items-center gap-[4px] px-[8px] pb-[4px] pt-[12px] text-[11.5px] font-medium text-[color:var(--muted-soft)]">
                            {g.tier === IN_USE_TIER ? (
                                <>In use<HelpTip label="What Chat and Tasks mean" text={IN_USE_HELP} /></>
                            ) : <>{tierLabel(g.tier)}<span className="ml-[3px] tabular-nums font-normal opacity-80">{g.items.length}</span></>}
                        </div>
                        {g.items.map(({ model, isDefault }) => (
                            <ModelRow
                                key={model.id}
                                model={model}
                                query={search.trim().toLowerCase()}
                                isDefault={isDefault}
                                isChat={model.id === chatModelId}
                                isTasks={model.id === taskModelId}
                                taskIsAuto={taskIsAuto}
                                disabled={disabled}
                                onUse={() => onUse(model.id)}
                                onUseForTasks={() => onUseForTasks(model.id)}
                            />
                        ))}
                    </div>
                ))}
            </div>
        </>
    );
}
